import React from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import Avatar from "../../Components/Avatar";
import {
  formatIdeaDate,
  formatSubmitterDisplayName,
} from "./ideaDetailUiUtils";
import { SPARK_OVERVIEW_FIELDS } from "./ideaFormConfig";

const hasText = (value) => Boolean(String(value || "").trim());

// Flat content section: bold heading + paragraph (matches Figma overview)
const Section = ({ title, value }) => (
  <div className="flex flex-col gap-1.5">
    <Typography as="h4" className="text-base font-semibold text-primary-900">
      {title}
    </Typography>
    {hasText(value) ? (
      <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
        {value}
      </Typography>
    ) : (
      <Typography as="p" className="text-sm text-slate-400 italic">
        Not provided yet
      </Typography>
    )}
  </div>
);

const InsightCard = ({ title, description }) => (
  <Section title={title} value={description} />
);

const ChecklistGroup = ({ title, items }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <Typography as="h4" className="text-sm font-bold text-slate-800 mb-4">
      {title}
    </Typography>
    <div className="flex flex-col gap-2.5">
      {items.map(({ label, checked, extra }) => (
        <label
          key={label}
          className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2.5"
        >
          <input
            type="checkbox"
            checked={Boolean(checked)}
            readOnly
            className="h-4 w-4 rounded border-slate-300 text-violet-600"
          />
          <Typography as="p" className="text-sm text-slate-800 flex-1">
            {label}
          </Typography>
          {extra}
        </label>
      ))}
    </div>
  </div>
);

const MARKET_BLOCKS = [
  { key: "idealCustomerProfile", title: "Ideal Customer Profile", icon: "lucide:users-round" },
  { key: "valueProposition", title: "Value Proposition", icon: "lucide:gem" },
  { key: "productMarketFit", title: "Product Market Fit", icon: "lucide:target" },
  { key: "targetAddressableMarket", title: "Target Addressable Market", icon: "lucide:globe-2" },
];

const EXECUTION_BLOCKS = [
  { key: "mvp", title: "MVP", icon: "lucide:box" },
  { key: "marketValidation", title: "Market Validation", icon: "lucide:chart-line" },
  { key: "financialProjections", title: "Financial Projections", icon: "lucide:calculator" },
  { key: "microGrantProposal", title: "Micro-Grant Proposal", icon: "lucide:wallet" },
];

const GROWTH_BLOCKS = [
  { key: "enhanceBusinessModel", title: "Enhance Business Model", icon: "lucide:layers" },
  { key: "productReadiness", title: "Product Readiness", icon: "lucide:package-check" },
  { key: "customerProfile", title: "Customer Profile", icon: "lucide:user-circle" },
  { key: "gtmStrategy", title: "GTM Strategy", icon: "lucide:rocket" },
  { key: "productMarketFit", title: "Product Market Fit", icon: "lucide:target" },
  { key: "targetAddressableMarket", title: "Target Addressable Market", icon: "lucide:globe-2" },
  { key: "customerFeedback", title: "Customer Feedback", icon: "lucide:message-square-heart" },
];

const IdeaOverviewSection = ({
  engagement,
  activeStage,
  canEdit = false,
  readOnlyOverviewFieldKeys = [],
  formValues = {},
  onFieldChange,
  onSave,
  saving = false,
  hideSaveButton = false,
}) => {
  const submitter = engagement.submitter;
  const submitterDisplayName = formatSubmitterDisplayName(submitter) || "Innovator";
  const statusUpper = String(engagement?.status || "").toUpperCase();
  const isAccepted = statusUpper === "ACTIVE";
  const isRejected = statusUpper === "REJECTED";
  const decisionReason = engagement?.decisionReason || engagement?.rejectionMessage || "";
  const readOnlyKeys = new Set(readOnlyOverviewFieldKeys);
  const submitterAvatar =
    submitter?.avatar &&
    submitter.avatar.length >= 10 &&
    (submitter.avatar.startsWith("http") ||
      submitter.avatar.startsWith("data:image") ||
      submitter.avatar.startsWith("/"))
      ? submitter.avatar
      : null;

  const renderBlockGrid = (sectionKey, blocks, title, subtitle) => {
    const section = engagement[sectionKey];
    if (!section) return null;

    return (
      <section className="flex flex-col gap-4">
        <div>
          <Typography as="h3" className="text-base font-bold text-slate-900">
            {title}
          </Typography>
          {subtitle && (
            <Typography as="p" className="text-sm text-slate-500 mt-1">
              {subtitle}
            </Typography>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {blocks.map(({ key, title: blockTitle, icon }) => {
            const item = section[key];
            if (!item) return null;
            return (
              <InsightCard
                key={key}
                icon={icon}
                title={blockTitle}
                description={item.description}
              />
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="flex flex-col gap-6 text-left">
      {/* Submitted row */}
      <div className="flex items-center justify-between w-full flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-600" />
          <Typography as="p" className="text-sm font-medium text-violet-600">
            Submitted: {formatIdeaDate(engagement.submissionDate) || "—"}
          </Typography>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-sm font-medium text-violet-600 hover:bg-violet-100 transition-colors"
          >
            <Icon icon="lucide:info" className="h-4 w-4" />
            Rubrics at this stage
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white">
            <Avatar
              src={submitterAvatar}
              firstName={submitter?.name?.split(" ")[0]}
              lastName={submitter?.name?.split(" ").slice(1).join(" ")}
              email={submitter?.email}
              alt={submitter?.name}
              size="w-6 h-6"
            />
            <Typography as="span" className="text-sm font-medium text-slate-700">
              {submitterDisplayName}
            </Typography>
          </div>
        </div>
      </div>

      {canEdit ? (
        <>
          <section className="flex flex-col gap-4">
            <Typography as="h3" className="text-base font-bold text-slate-900">
              Idea details
            </Typography>
            <div className="grid grid-cols-1 gap-4">
              {SPARK_OVERVIEW_FIELDS.map(({ key, label, icon, rows, hint }) => {
                const fieldReadOnly = readOnlyKeys.has(key);
                return (
                <div
                  key={key}
                  className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 flex flex-col gap-2"
                >
                  <div className="flex items-center gap-2">
                    <Icon icon={icon} className="h-4 w-4 text-violet-600" />
                    <label className="text-sm font-semibold text-slate-800">{label}</label>
                  </div>
                  {hint && <p className="text-xs text-slate-400">{hint}</p>}
                  {fieldReadOnly && key === "problem_statement" && (
                    <p className="text-xs text-slate-500">
                      This field cannot be edited after it is saved. Contact your mentor if you need a change.
                    </p>
                  )}
                  <textarea
                    rows={rows || 3}
                    value={formValues[key] || ""}
                    readOnly={fieldReadOnly}
                    onChange={(e) => {
                      if (fieldReadOnly) return;
                      onFieldChange(key, e.target.value);
                    }}
                    className={`w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-200 ${
                      fieldReadOnly
                        ? "bg-slate-100 text-slate-600 cursor-not-allowed"
                        : "bg-white text-slate-800"
                    }`}
                    placeholder={`Enter ${label.toLowerCase()}...`}
                  />
                </div>
              );
              })}
            </div>
          </section>

          {!hideSaveButton && (
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={onSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700"
                startIcon={
                  saving ? (
                    <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin" />
                  ) : (
                    <Icon icon="lucide:save" className="h-4 w-4" />
                  )
                }
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-6 max-w-3xl">
          <Section
            title="Problem Statement"
            value={engagement.problemStatement || engagement.description}
          />
          <Section
            title="Proposed Solution"
            value={engagement.proposedSolution || engagement.description}
          />
          <Section title="Target Audience" value={engagement.targetAudience} />
          <Section title="Existing Alternatives" value={engagement.existingAlternatives} />
          <Section title="Primary Impact" value={engagement.primaryImpact} />
        </div>
      )}

      {isAccepted && hasText(decisionReason) && (
        <div className="max-w-3xl rounded-xl border border-emerald-200 bg-emerald-50/50 p-5 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon icon="lucide:badge-check" className="h-5 w-5 text-emerald-600" />
            <Typography as="h4" className="text-base font-semibold text-primary-900">
              Acceptance Reason
            </Typography>
          </div>
          <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-line">
            {decisionReason}
          </Typography>
        </div>
      )}

      {isRejected && hasText(decisionReason) && (
        <div className="max-w-3xl rounded-xl border border-red-200 bg-red-50/50 p-5 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon icon="lucide:x-circle" className="h-5 w-5 text-red-600" />
            <Typography as="h4" className="text-base font-semibold text-primary-900">
              Rejection Reason
            </Typography>
          </div>
          <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed whitespace-pre-line">
            {decisionReason}
          </Typography>
        </div>
      )}

      {!canEdit && (
        <>

      {activeStage >= 2 &&
        renderBlockGrid(
          "marketStrategy",
          MARKET_BLOCKS,
          "Market strategy",
          "Customer, value, fit, and market sizing for this stage."
        )}

      {activeStage >= 3 &&
        renderBlockGrid(
          "executionPlan",
          EXECUTION_BLOCKS,
          "Execution plan",
          "MVP, validation, finances, and grant materials."
        )}

      {activeStage >= 4 &&
        renderBlockGrid(
          "growthStrategy",
          GROWTH_BLOCKS,
          "Growth strategy",
          "Business model, readiness, GTM, and customer feedback."
        )}

      {activeStage >= 4 && engagement.pitchPreparation && (
        <section className="flex flex-col gap-4">
          <div>
            <Typography as="h3" className="text-base font-bold text-slate-900">
              Pitch preparation
            </Typography>
            <Typography as="p" className="text-sm text-slate-500 mt-1">
              Checklist items completed for the Shine stage.
            </Typography>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChecklistGroup
              title="Investor pitch"
              items={[
                {
                  label: "Problem statement",
                  checked: engagement.pitchPreparation.investorPitch?.problemStatement,
                },
                {
                  label: "Product market fit",
                  checked: engagement.pitchPreparation.investorPitch?.productMarketFit,
                },
                {
                  label: "Customer profile",
                  checked: engagement.pitchPreparation.investorPitch?.customerProfile,
                },
                {
                  label: "Competitive landscape",
                  checked: engagement.pitchPreparation.investorPitch?.competitiveLandscape,
                },
                {
                  label: "Target addressable market",
                  checked: engagement.pitchPreparation.investorPitch?.targetAddressableMarket,
                },
              ]}
            />
            <ChecklistGroup
              title="Market traction & metrics"
              items={[
                {
                  label: "Revenue",
                  checked: engagement.pitchPreparation.marketTraction?.revenue,
                },
                {
                  label: "Margin",
                  checked: engagement.pitchPreparation.marketTraction?.margin,
                },
                {
                  label: "Sales",
                  checked: engagement.pitchPreparation.metrics?.sales,
                },
                {
                  label: "Pipeline",
                  checked: engagement.pitchPreparation.metrics?.pipeline,
                },
                {
                  label: "Marketing",
                  checked: engagement.pitchPreparation.metrics?.marketing,
                },
                {
                  label: "Followers",
                  checked: engagement.pitchPreparation.metrics?.followers,
                },
                {
                  label: "Customer acquisition cost",
                  checked: engagement.pitchPreparation.metrics?.customerAcquisitionCost,
                },
              ]}
            />
          </div>
          <ChecklistGroup
            title="Additional materials"
            items={[
              {
                label: "Product / video link",
                checked: !!engagement.pitchPreparation.additional?.productVideoLink,
              },
              {
                label: "Pitch deck upload",
                checked: !!engagement.pitchPreparation.additional?.pitchDeckUpload,
              },
            ]}
          />
        </section>
      )}
        </>
      )}
    </div>
  );
};

export default IdeaOverviewSection;
